import React, { useEffect, useRef } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { XIcon } from 'lucide-react';
import type { LegalDocument } from '../data/legal';
type LegalModalProps = {
  document: LegalDocument | null;
  onClose: () => void;
};
export function LegalModal({
  document: doc,
  onClose
}: LegalModalProps) {
  const closeRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (!doc) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    const previousOverflow = window.document.body.style.overflow;
    window.document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKeyDown);
    closeRef.current?.focus();
    return () => {
      window.document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [doc, onClose]);
  return <AnimatePresence data-id="element-104" data-mp="1cuadx1di39qh">
      {doc && <div className="fixed inset-0 z-50 flex items-end justify-center p-0 sm:items-center sm:p-6" role="dialog" aria-modal="true" aria-labelledby="legal-modal-title" data-id="element-105" data-mp="1ct91asgu6f6n">
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2,
        ease: [0.23, 1, 0.32, 1]
      }} onClick={onClose} className="absolute inset-0 bg-ink/50 backdrop-blur-sm" data-id="element-106" data-mp="1frmbds0lg35n" />

          <motion.div initial={{
        opacity: 0,
        scale: 0.96,
        y: 12
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.96,
        y: 12
      }} transition={{
        duration: 0.25,
        ease: [0.23, 1, 0.32, 1]
      }} className="relative flex max-h-[85vh] w-full max-w-2xl flex-col overflow-hidden rounded-t-3xl bg-cream shadow-2xl shadow-ink/25 sm:rounded-3xl" data-id="element-107" data-mp="ry47chyay49u">
            <header className="flex items-start justify-between gap-6 border-b border-ink/10 px-6 py-5 sm:px-8" data-id="element-108" data-mp="e80ps4adayqq">
              <div data-id="element-109" data-mp="672t43xu93k6">
                <h2 id="legal-modal-title" className="font-display text-2xl font-semibold tracking-tight text-ink" data-id="element-110" data-mp="66jk5cq61pia">
                  {doc.title}
                </h2>
                <p className="mt-1 text-xs text-ink/50" data-id="element-111" data-mp="fww8lg13q2tp">{doc.updated}</p>
              </div>
              <button ref={closeRef} type="button" onClick={onClose} aria-label="Cerrar" className="rounded-full p-2 text-ink/60 transition-colors duration-150 ease-smooth hover:bg-ink/5 hover:text-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-teal" data-id="element-112" data-mp="azcr8793x9xj">
                <XIcon className="h-5 w-5" aria-hidden="true" data-id="element-113" data-mp="3od7oh0de68mw" />
              </button>
            </header>

            <div className="overflow-y-auto px-6 py-6 sm:px-8" data-id="element-114" data-mp="18la5fsgget3o">
              <p className="text-base leading-relaxed text-ink/75" data-id="element-115" data-mp="13nid4xbwd4yy">
                {doc.intro}
              </p>

              <div className="mt-8 space-y-8" data-id="element-116" data-mp="1b683t7mxib8x">
                {doc.sections.map(section => <section key={section.heading} data-id="element-117" data-mp="1g6dq08afj5jm">
                    <h3 className="font-display text-lg font-semibold text-ink" data-id="element-118" data-mp="1g75i394til55">
                      {section.heading}
                    </h3>
                    {section.paragraphs.map(paragraph => <p key={paragraph} className="mt-3 text-sm leading-relaxed text-ink/70" data-id="element-119" data-mp="169r73p1nyok8">
                        {paragraph}
                      </p>)}
                  </section>)}
              </div>
            </div>
          </motion.div>
        </div>}
    </AnimatePresence>;
}